import React, { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { onValue, ref, remove, serverTimestamp, update } from 'firebase/database';
import { formatDistanceToNow } from 'date-fns';
import { auth, database } from '../firebase/firebaseConfig';
import {
  Card,
  CardContent,
  StyledList,
  StyledListItem,
  Button,
  ModalContainer,
  ModalContent,
  Textarea,
} from './StyledComponents';

const resolveTimestamp = (report) => {
  if (!report) return 0;
  const { createdAt, createdAtClient } = report;
  if (typeof createdAt === 'number') {
    return createdAt;
  }
  if (createdAt && typeof createdAt === 'object' && typeof createdAtClient === 'number') {
    return createdAtClient;
  }
  if (typeof createdAtClient === 'number') {
    return createdAtClient;
  }
  return 0;
};

const BugReportsWidget = ({ style, isAdmin }) => {
  const [userId, setUserId] = useState(auth.currentUser?.uid || null);
  const [reportsByUser, setReportsByUser] = useState({});
  const [userDirectory, setUserDirectory] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const [responseDraft, setResponseDraft] = useState('');
  const [savingResponse, setSavingResponse] = useState(false);
  const [responseFeedback, setResponseFeedback] = useState({ type: null, message: '' });
  const [copyingMetadata, setCopyingMetadata] = useState(false);
  const [metadataCopyFeedback, setMetadataCopyFeedback] = useState({ type: null, message: '' });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid || null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!userId || isAdmin) {
      if (!userId && !isAdmin) {
        setReportsByUser({});
      }
      return undefined;
    }

    const reportsRef = ref(database, `users/${userId}/bugReports`);
    const unsubscribe = onValue(reportsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const parsed = Object.entries(data).map(([id, value]) => ({
        id,
        ...value,
      }));
      setReportsByUser({ [userId]: parsed });
    });

    return () => unsubscribe();
  }, [userId, isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setUserDirectory({});
      return undefined;
    }

    const directoryRef = ref(database, 'userDirectory');
    const unsubscribe = onValue(directoryRef, (snapshot) => {
      setUserDirectory(snapshot.val() || {});
    });

    return () => unsubscribe();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    const userIds = Object.keys(userDirectory || {});

    if (!userIds.length) {
      setReportsByUser({});
      return undefined;
    }

    setReportsByUser((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((uid) => {
        if (!userIds.includes(uid)) {
          delete next[uid];
        }
      });
      return next;
    });

    const unsubscribers = userIds.map((uid) => {
      const reportsRef = ref(database, `users/${uid}/bugReports`);
      return onValue(reportsRef, (snapshot) => {
        const data = snapshot.val() || {};
        const parsed = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
        }));
        setReportsByUser((prev) => {
          const next = { ...prev };
          if (parsed.length) {
            next[uid] = parsed;
          } else {
            delete next[uid];
          }
          return next;
        });
      });
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [isAdmin, userDirectory]);

  const reports = useMemo(() => {
    const entries = Object.entries(reportsByUser);
    if (!entries.length) {
      return [];
    }

    const aggregated = entries.flatMap(([ownerUid, userReports]) => {
      const directoryEntry = userDirectory?.[ownerUid] || {};
      return userReports.map((report) => ({
        ...report,
        ownerUserId: ownerUid,
        ownerDisplayName: directoryEntry.displayName || 'Unknown user',
        ownerEmail: directoryEntry.email || null,
      }));
    });

    aggregated.sort((a, b) => resolveTimestamp(b) - resolveTimestamp(a));
    return aggregated;
  }, [reportsByUser, userDirectory]);

  const openReports = useMemo(
    () => reports.filter((report) => report.status !== 'resolved'),
    [reports]
  );
  const resolvedReports = useMemo(
    () => reports.filter((report) => report.status === 'resolved'),
    [reports]
  );

  useEffect(() => {
    if (!selectedReport) {
      setResponseDraft('');
      setResponseFeedback({ type: null, message: '' });
      setMetadataCopyFeedback({ type: null, message: '' });
      setCopyingMetadata(false);
      return;
    }
    setResponseDraft(selectedReport.adminResponse?.message || '');
    setResponseFeedback({ type: null, message: '' });
    setMetadataCopyFeedback({ type: null, message: '' });
    setCopyingMetadata(false);
  }, [selectedReport]);

  useEffect(() => {
    if (!selectedReport) return;
    const updated = reports.find(
      (report) => report.id === selectedReport.id && report.ownerUserId === selectedReport.ownerUserId
    );
    if (!updated) {
      setSelectedReport(null);
      return;
    }
    const hasChanges =
      updated.status !== selectedReport.status ||
      resolveTimestamp(updated) !== resolveTimestamp(selectedReport) ||
      (updated.resolvedAt || null) !== (selectedReport.resolvedAt || null) ||
      (updated.adminResponse?.message || '') !== (selectedReport.adminResponse?.message || '') ||
      resolveResponseTimestamp(updated.adminResponse) !== resolveResponseTimestamp(selectedReport.adminResponse);
    if (hasChanges) {
      setSelectedReport(updated);
    }
  }, [reports, selectedReport]);

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    try {
      return `${formatDistanceToNow(new Date(timestamp))} ago`;
    } catch {
      return 'Unknown time';
    }
  };

  const metadataPayload = useMemo(() => {
    if (!selectedReport) return null;
    const payload = {};
    if (selectedReport.context) {
      payload.context = selectedReport.context;
    }
    if (selectedReport.environment) {
      payload.environment = selectedReport.environment;
    }
    return Object.keys(payload).length ? payload : null;
  }, [selectedReport]);

  const metadataText = useMemo(() => {
    if (!metadataPayload) return '';
    try {
      return JSON.stringify(metadataPayload, null, 2);
    } catch {
      return '';
    }
  }, [metadataPayload]);

  const resolveTargetUserId = (report) => {
    if (!report) return null;
    if (isAdmin) {
      return report.ownerUserId || report.reportedForUserId || report.reporterUid || null;
    }
    return userId;
  };

  const resolveResponseTimestamp = (response) => {
    if (!response) return 0;
    const { updatedAt, updatedAtClient } = response;
    if (typeof updatedAt === 'number') {
      return updatedAt;
    }
    if (typeof updatedAtClient === 'number') {
      return updatedAtClient;
    }
    return 0;
  };

  const handleToggleStatus = async (report) => {
    if (!report?.id || inFlight) return;
    const targetUserId = resolveTargetUserId(report);
    if (!targetUserId) return;
    const newStatus = report.status === 'resolved' ? 'open' : 'resolved';
    const updates = { status: newStatus };
    if (newStatus === 'resolved') {
      updates.resolvedAt = serverTimestamp();
    } else {
      updates.resolvedAt = null;
    }
    setInFlight(true);
    try {
      await update(ref(database, `users/${targetUserId}/bugReports/${report.id}`), updates);
      if (selectedReport?.id === report.id) {
        setSelectedReport((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: newStatus,
            resolvedAt: newStatus === 'resolved' ? Date.now() : null,
            ownerUserId: targetUserId,
          };
        });
      }
    } catch (error) {
      console.error('Failed to update bug report status', error);
    } finally {
      setInFlight(false);
    }
  };

  const handleDeleteReport = async (report) => {
    if (!report?.id || inFlight) return;
    const targetUserId = resolveTargetUserId(report);
    if (!targetUserId) return;
    const shouldDelete = window.confirm('Delete this bug report? This action cannot be undone.');
    if (!shouldDelete) return;
    setInFlight(true);
    try {
      await remove(ref(database, `users/${targetUserId}/bugReports/${report.id}`));
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to delete bug report', error);
    } finally {
      setInFlight(false);
    }
  };

  const handleCopyMetadata = async () => {
    if (!metadataText || copyingMetadata) return;
    setCopyingMetadata(true);
    setMetadataCopyFeedback({ type: null, message: '' });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(metadataText);
        setMetadataCopyFeedback({ type: 'success', message: 'Copied to clipboard.' });
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (error) {
      console.error('Failed to copy bug report metadata', error);
      setMetadataCopyFeedback({ type: 'error', message: 'Unable to copy. Please copy manually.' });
    } finally {
      setCopyingMetadata(false);
    }
  };

  const handleSaveResponse = async () => {
    if (!isAdmin || !selectedReport || savingResponse) return;
    const targetUserId = resolveTargetUserId(selectedReport);
    if (!targetUserId) return;
    const trimmed = responseDraft.trim();
    const hasContent = trimmed.length > 0;
    setSavingResponse(true);
    setResponseFeedback({ type: null, message: '' });
    try {
      const responsePayload = hasContent
        ? {
            message: trimmed,
            responderUid: userId || null,
            responderEmail: auth.currentUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedAtClient: Date.now(),
          }
        : null;

      await update(ref(database, `users/${targetUserId}/bugReports/${selectedReport.id}`), {
        adminResponse: responsePayload,
      });

      if (responsePayload) {
        setSelectedReport((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            adminResponse: {
              ...responsePayload,
              updatedAt: Date.now(),
            },
          };
        });
        setResponseFeedback({ type: 'success', message: 'Response saved.' });
      } else {
        setSelectedReport((prev) => {
          if (!prev) return prev;
          return { ...prev, adminResponse: null };
        });
        setResponseFeedback({ type: 'success', message: 'Response cleared.' });
      }
    } catch (error) {
      console.error('Failed to save bug report response', error);
      setResponseFeedback({ type: 'error', message: 'Failed to save response. Please try again.' });
    } finally {
      setSavingResponse(false);
    }
  };

  const renderReportRow = (report) => {
    const createdAt = resolveTimestamp(report);
    return (
      <StyledListItem key={report.id} style={{ alignItems: 'flex-start' }}>
        <div>
          <strong>{report.summary || '(No summary)'}</strong>
          <div style={{ fontSize: '0.85em', color: '#777' }}>
            Submitted {formatRelativeTime(createdAt)}
            {report.reporterEmail && ` · ${report.reporterEmail}`}
            {isAdmin && (
              <>
                {' '}
                · For {report.ownerDisplayName || report.ownerUserId}
                {report.ownerEmail ? ` (${report.ownerEmail})` : ''}
              </>
            )}
          </div>
          {report.details && (
            <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#555', maxWidth: '420px' }}>
              {report.details.length > 140
                ? `${report.details.slice(0, 140)}…`
                : report.details}
            </div>
          )}
          {report.adminResponse?.message && (
            <div style={{ marginTop: '6px', fontSize: '0.8em', color: '#4d7c0f' }}>
              Admin responded {formatRelativeTime(resolveResponseTimestamp(report.adminResponse))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button onClick={() => setSelectedReport(report)}>View</Button>
          <Button onClick={() => handleToggleStatus(report)} disabled={inFlight}>
            {report.status === 'resolved' ? 'Reopen' : 'Resolve'}
          </Button>
        </div>
      </StyledListItem>
    );
  };

  const detailBoxStyle = {
    background: 'rgba(12, 23, 44, 0.92)',
    color: '#e2e8f0',
    border: '1px solid rgba(148, 163, 184, 0.4)',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '12px',
    fontSize: '0.95em',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    maxHeight: '240px',
    overflowY: 'auto',
  };

  const infoBoxStyle = {
    ...detailBoxStyle,
    fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: '0.85em',
  };

  return (
    <Card style={style}>
      <h2>Bug Reports</h2>
      <CardContent style={{ overflowY: 'auto', maxHeight: '360px' }}>
        <h3>Open</h3>
        <StyledList>
          {openReports.length > 0
            ? openReports.slice(0, 10).map(renderReportRow)
            : <StyledListItem>No open reports</StyledListItem>}
        </StyledList>
        <h3>Resolved</h3>
        <StyledList>
          {resolvedReports.length > 0
            ? resolvedReports.slice(0, 5).map(renderReportRow)
            : <StyledListItem>No resolved reports</StyledListItem>}
        </StyledList>
      </CardContent>

      {selectedReport && (
        <ModalContainer onClick={() => setSelectedReport(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>{selectedReport.summary || 'Bug Report'}</h3>
            <div style={{ marginBottom: '8px', fontSize: '0.9em', color: '#555' }}>
              Submitted {formatRelativeTime(resolveTimestamp(selectedReport))}
              {selectedReport.reporterEmail && ` by ${selectedReport.reporterEmail}`}
              {isAdmin && selectedReport.ownerDisplayName && (
                <>
                  {' '}
                  · For {selectedReport.ownerDisplayName}
                  {selectedReport.ownerEmail ? ` (${selectedReport.ownerEmail})` : ''}
                </>
              )}
            </div>
            <section style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 6px 0' }}>Details</h4>
              <div style={detailBoxStyle}>
                {selectedReport.details || 'No additional details provided.'}
              </div>
            </section>
            {metadataText && (
              <section style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 6px 0' }}>Metadata</h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <Button onClick={handleCopyMetadata} disabled={copyingMetadata}>
                    {copyingMetadata ? 'Copying…' : 'Copy to Clipboard'}
                  </Button>
                </div>
                {metadataCopyFeedback.type === 'error' && (
                  <div style={{ color: '#c26166', fontSize: '0.85em', marginBottom: '6px' }}>
                    {metadataCopyFeedback.message}
                  </div>
                )}
                {metadataCopyFeedback.type === 'success' && (
                  <div style={{ color: '#68c174', fontSize: '0.85em', marginBottom: '6px' }}>
                    {metadataCopyFeedback.message}
                  </div>
                )}
                <div style={infoBoxStyle}>
                  {metadataText}
                </div>
              </section>
            )}
            {selectedReport.adminResponse?.message && (
              <section style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 6px 0' }}>Admin Response</h4>
                <div style={detailBoxStyle}>
                  {selectedReport.adminResponse.message}
                  <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#94a3b8' }}>
                    Last updated {formatRelativeTime(resolveResponseTimestamp(selectedReport.adminResponse))}
                    {selectedReport.adminResponse.responderEmail && ` · ${selectedReport.adminResponse.responderEmail}`}
                  </div>
                </div>
              </section>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button onClick={() => handleToggleStatus(selectedReport)} disabled={inFlight}>
                {selectedReport.status === 'resolved' ? 'Reopen' : 'Resolve'}
              </Button>
              {selectedReport.status === 'resolved' && (
                <Button onClick={() => handleDeleteReport(selectedReport)} disabled={inFlight}>
                  Delete
                </Button>
              )}
              <Button onClick={() => setSelectedReport(null)}>Close</Button>
            </div>
            {isAdmin && (
              <section style={{ marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 6px 0' }}>Respond to reporter</h4>
                <Textarea
                  value={responseDraft}
                  onChange={(e) => setResponseDraft(e.target.value)}
                  placeholder="Write a reply that will be visible to the reporting user"
                  style={{ width: '100%', minHeight: '120px', marginBottom: '8px' }}
                  disabled={savingResponse}
                />
                {responseFeedback.type === 'error' && (
                  <div style={{ color: '#c26166', fontSize: '0.85em', marginBottom: '6px' }}>
                    {responseFeedback.message}
                  </div>
                )}
                {responseFeedback.type === 'success' && (
                  <div style={{ color: '#68c174', fontSize: '0.85em', marginBottom: '6px' }}>
                    {responseFeedback.message}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button onClick={handleSaveResponse} disabled={savingResponse}>
                    {savingResponse ? 'Saving…' : responseDraft.trim() ? 'Save Response' : 'Clear Response'}
                  </Button>
                  <Button
                    onClick={() => setResponseDraft(selectedReport.adminResponse?.message || '')}
                    disabled={savingResponse}
                  >
                    Reset
                  </Button>
                </div>
              </section>
            )}
          </ModalContent>
        </ModalContainer>
      )}
    </Card>
  );
};

export default BugReportsWidget;
